import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default content to show if nothing is in the database
const defaultFoundersImage = "https://customer-assets.emergentagent.com/job_spock-clone/artifacts/cdfwxmpx_Screenshot%202025-12-28%20at%208.20.08%E2%80%AFPM.png";
const defaultFoundersNames = "David Knight & Julie Nimoy";
const defaultContent = `Producing team David Knight and Julie Nimoy founded the Nimoy Knight Foundation after Leonard's passing as a way to honor his legacy, beginning with fighting COPD, the disease that eventually took his life.

"We've always had a passion for biographies and health-related topics says co-producer, David Knight".

Health Point Productions was created in 2011 to produce films that reflect these interests.

Recent films include "Remembering Leonard Nimoy" and "Remembering Gene Wilder."

"Remembering Leonard Nimoy" tells the heartwarming story of Leonard Nimoy's life and legacy while raising awareness for chronic obstructive pulmonary disease (COPD), to which he succumbed in 2015. This award-winning PBS documentary has also received widespread praise from both the public and healthcare professionals worldwide.

David and Julie decided to expand the foundation's mission in commemoration of the 10th anniversary of Leonard's passing by partnering with the autism community to honor the bond many autistic people felt with Leonard's beloved "Mr. Spock" character.

David and Julie currently reside in Los Angeles, CA.`;

const AboutPage = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/about-content`);
        if (response.data) {
          setAboutData(response.data);
        }
      } catch (err) {
        console.error('Error fetching about content:', err);
        // Use defaults if fetch fails
      } finally {
        setLoading(false);
      }
    };

    fetchAboutContent();
  }, []);

  // Use fetched data or fall back to defaults
  const foundersImage = aboutData?.founders_image || defaultFoundersImage;
  const foundersNames = aboutData?.founders_names || defaultFoundersNames;
  const contentText = aboutData?.content || defaultContent;

  // Split content by paragraphs for rendering
  const paragraphs = contentText.split('\n').filter(p => p.trim());

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b]">
        <Navbar />
        <div className="flex justify-center items-center py-40">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          <span className="ml-3 text-gray-400">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section with Founders Photo */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Founders Image */}
          <div className="mb-10">
            <img
              src={foundersImage}
              alt={`${foundersNames} - Co-Founders of the Nimoy-Knight Foundation`}
              className="w-full max-w-2xl mx-auto rounded-2xl shadow-2xl"
              loading="eager"
            />
          </div>

          {/* Co-Founders Header */}
          <div className="text-center mb-8">
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
              Co-Founders
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2">
              {foundersNames}
            </h1>
          </div>

          {/* Content */}
          <div className="prose prose-invert max-w-none">
            {paragraphs.map((paragraph, index) => (
              <p 
                key={index} 
                className={`text-gray-300 text-lg leading-relaxed mb-6 ${
                  paragraph.startsWith('"') && paragraph.endsWith('"') ? 'italic' : ''
                }`}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Watch the Documentary
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Learn more about Leonard Nimoy's extraordinary life and career in the documentary film.
          </p>
          <Link
            to="/film"
            className="inline-flex items-center justify-center px-10 py-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105 text-lg"
          >
            Explore the Film
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
